/**
 * GameMenu — game / settings menu for the active game.
 *
 * Renders as a cog icon button (configurable via `trigger` prop) that
 * opens a floating panel containing game-level navigation + resign. Its
 * Settings action swaps the game menu for the shared settings panel.
 * The Region Details overlay toggle lives on the map itself (floating chip),
 * not in this menu.
 *
 * Forfeit: "Forfeit Game" swaps the menu for the Forfeit game panel (ForfeitPanel,
 * same modal like Settings/Config): the two ways to forfeit, "More about resigning",
 * "More about abandoning" and "Back to the game"; each option explains its outcome
 * with its own confirm ("Confirm resign" / "Confirm abandon") and a Back. The active
 * panel is dismissed by the explicit Close button (top-right ✕), the
 * platform back action, or the browser back/swipe gesture on web.
 */

import React, { useState } from 'react'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
} from 'react-native'
import { useRouter } from 'expo-router'
import { useAbandonGame, useGames, useResign } from '../../hooks/useGames'
import { useAuth } from '../../hooks/useAuth'
import { gameMenuAttentionGames } from '../../lib/gameMenuAttention'
import { CountBubble } from '../ui/CountBubble'
import { useTheme } from '../../hooks/useTheme'
import { useWebModalHistory } from '../../hooks/useWebModalHistory'
import { SettingsHubContent } from '../settings/SettingsHubContent'
import { QaThemeToggle } from '../settings/ThemeToggle'
import { SupportUnreadBadge } from '../settings/SupportUnreadBadge'
import { GameRulesPanelContent } from './GameRulesModal'
import { FloatingGamePanel } from './FloatingGamePanel'
import { ForfeitPanel, type ForfeitStep } from './ForfeitPanel'
import type { GameState } from '../../lib/gameState'
import { useSupportUnread } from '../../hooks/useSupportInbox'

interface GameMenuProps {
  gameId: string
  /** Whether the viewer can resign (member of an ACTIVE game, not yet eliminated). */
  canResign: boolean
  hasSpecialRules?: boolean
  /** Per-game opt-in contextual help shown beneath the map. */
  tutorialEnabled?: boolean
  onTutorialEnabledChange?: (enabled: boolean) => void
  /** Hydrated state rendered by the in-menu Game Config modal. */
  gameState?: GameState | null
  /** Locked roster size for the active game. */
  playerCount?: number
  bootAfterSeconds?: number
  /** Opens the shared per-game dice luck surface after this menu closes. */
  onShowDiceLuck?: () => void
  /**
   * Optional render-prop for the trigger button. Lets the parent
   * position a cog overlay on top of the map. When omitted, falls back
   * to the legacy in-flow "⋮" chip.
   */
  trigger?: (open: () => void, attentionCount: number) => React.ReactNode
}

export function GameMenu({
  gameId,
  canResign,
  hasSpecialRules = false,
  tutorialEnabled = false,
  onTutorialEnabledChange = () => {},
  gameState,
  playerCount,
  bootAfterSeconds,
  onShowDiceLuck,
  trigger,
}: GameMenuProps) {
  const [open, setOpen] = useState(false)
  const supportUnread = useSupportUnread().data?.count ?? 0
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  // Forfeit flow: null (closed) → 'choose' (two ways, explained) → 'resign' | 'abandon' (details + confirm).
  const [forfeitStep, setForfeitStep] = useState<ForfeitStep | null>(null)
  const [tutorialTogglePending, setTutorialTogglePending] = useState(false)
  const [attentionGameIndex, setAttentionGameIndex] = useState(0)
  const tutorialToggleTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const resign = useResign(gameId)
  const abandon = useAbandonGame(gameId)
  const { theme } = useTheme()
  const actionIconColor = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const resignColors = theme === 'dark'
    ? { backgroundColor: '#3d321e', borderColor: '#ef4444' }
    : { backgroundColor: '#fff3c4', borderColor: '#b45309' }
  const { user } = useAuth()
  const { data: games } = useGames()
  const otherAttentionGames = gameMenuAttentionGames(games ?? [], user?.id, gameId)
  const attentionCount = otherAttentionGames.length + supportUnread
  const currentGame = (games ?? []).find((game) => game.id === gameId)
  const specialGame = hasSpecialRules || !!gameState?.map.specialRules?.length
  const configPlayerCount = playerCount ?? currentGame?.players.length
  const activeAttentionGame = otherAttentionGames.length > 0
    ? otherAttentionGames[attentionGameIndex % otherAttentionGames.length]
    : undefined
  const activeGameIsInvite = activeAttentionGame?.viewerInvitationStatus === 'pending'
  const router = useRouter()

  function close() {
    if (tutorialToggleTimer.current) {
      clearTimeout(tutorialToggleTimer.current)
      tutorialToggleTimer.current = null
    }
    setOpen(false)
    // forfeitStep is reset in openMenu, not here — see the comment there.
  }

  function openMenu() {
    // Reset on opening, not closing: native modals keep drawing their children
    // during the fade-out, so restoring the label in close() makes it flash.
    setTutorialTogglePending(false)
    setSettingsOpen(false)
    setConfigOpen(false)
    setForfeitStep(null)
    setAttentionGameIndex(0)
    setOpen(true)
  }

  function cycleAttentionGame(direction: -1 | 1) {
    setAttentionGameIndex((current) => {
      if (otherAttentionGames.length < 2) return 0
      return (current + direction + otherAttentionGames.length) % otherAttentionGames.length
    })
  }

  // Keep the active panel mounted during the native modal's fade-out. Reset it
  // only on the next open so closing a nested panel cannot flash the game menu
  // underneath it.
  const modalVisible = open
  const closeLabel = settingsOpen
    ? 'Close settings'
    : configOpen
      ? 'Close game config'
      : 'Close menu'
  const dismissThenNavigate = useWebModalHistory({
    visible: modalVisible,
    canDismiss: true,
    onDismiss: close,
  })

  React.useEffect(() => {
    return () => {
      if (tutorialToggleTimer.current) clearTimeout(tutorialToggleTimer.current)
    }
  }, [])

  function toggleTutorial() {
    if (tutorialTogglePending) return
    setTutorialTogglePending(true)
    onTutorialEnabledChange(!tutorialEnabled)
    if (tutorialToggleTimer.current) clearTimeout(tutorialToggleTimer.current)
    tutorialToggleTimer.current = setTimeout(() => {
      tutorialToggleTimer.current = null
      close()
    }, 500)
  }

  async function handleAbandon() {
    try {
      await abandon.mutateAsync()
      close()
      // The seat is the AI's now; this player is no longer in the game.
      router.replace('/(app)')
    } catch {
      // Error surfaced inline in the panel via abandon.isError.
    }
  }

  async function handleResign() {
    try {
      await resign.mutateAsync()
      close()
    } catch {
      // Error surfaced inline in the panel via resign.isError.
    }
  }

  return (
    <>
      {trigger ? (
        trigger(openMenu, attentionCount)
      ) : (
        <TouchableOpacity
          onPress={openMenu}
          accessibilityLabel="Open game menu"
          accessibilityRole="button"
          className="p-2"
          style={{ minHeight: 40, minWidth: 40 }}
        >
          <Text className="text-ink text-xl">⋮</Text>
          {attentionCount > 0 && <View style={{ position: 'absolute', right: 0, top: 0 }}>
            <CountBubble count={attentionCount} />
          </View>}
        </TouchableOpacity>
      )}

      <FloatingGamePanel visible={modalVisible} onClose={close} closeLabel={closeLabel} closeTestID="game-menu-close">
          {settingsOpen ? (
            <ScrollView
              key="settings"
              testID="in-game-settings-modal"
              contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8, gap: 16 }}
            >
              <SettingsHubContent
                showTutorial={false}
                showQaTheme={false}
                navigateAfterDismiss={dismissThenNavigate}
              />
            </ScrollView>
          ) : configOpen ? (
            gameState ? <GameRulesPanelContent config={gameState.config} map={gameState.map}
              players={configPlayerCount == null ? '—' : String(configPlayerCount)} bootAfterSeconds={bootAfterSeconds} />
              : <Text className="font-typed text-ink-soft text-sm text-center py-4">Game config is unavailable.</Text>
          ) : forfeitStep !== null ? (
            <ScrollView key={forfeitStep} testID="forfeit-modal" contentContainerStyle={{ padding: 16, gap: 12 }}>
              <ForfeitPanel
                step={forfeitStep}
                onStep={setForfeitStep}
                onBackToGame={close}
                onResign={handleResign}
                onAbandon={handleAbandon}
                resignPending={resign.isPending}
                abandonPending={abandon.isPending}
                error={resign.error ?? abandon.error}
              />
            </ScrollView>
          ) : (
          <ScrollView key="menu" contentContainerStyle={{ padding: 16, gap: 12 }}>
            {/* Give Settings roughly a third of the navigation row.
                The close affordance remains outside the panel above. */}
            <View className="flex-row gap-2">
              <TouchableOpacity
                onPress={() => {
                  dismissThenNavigate(() => router.replace('/(app)'))
                }}
                accessibilityLabel="Back to lobby"
                accessibilityRole="button"
                testID="game-menu-back-to-lobby"
                className="bg-blood rounded-xl px-4 py-3 items-center justify-center"
                style={{ minHeight: 48, flex: 1 }}
              >
                <Text className="text-white font-stencil text-sm leading-5 uppercase">← Back to Lobby</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  setForfeitStep(null)
                  setConfigOpen(false)
                  setSettingsOpen(true)
                }}
                accessibilityLabel="Open settings"
                accessibilityRole="button"
                testID="game-menu-settings"
                className="bg-paper-up border border-olive rounded-xl py-3 flex-row gap-2 items-center justify-center"
                style={{ minHeight: 48, paddingHorizontal: 16, flexBasis: '35%', minWidth: 120, flexShrink: 0 }}
              >
                <View>
                  <MaterialCommunityIcons name="cog" size={16} color={actionIconColor} accessible={false} />
                  {supportUnread > 0 && <View style={{ position: 'absolute', right: -8, top: -12 }}><SupportUnreadBadge count={supportUnread} /></View>}
                </View>
                <Text className="text-ink font-stencil text-xs uppercase">Settings</Text>
              </TouchableOpacity>
            </View>

            <QaThemeToggle />

            {activeAttentionGame && (
              <View testID="game-menu-other-turns" className="gap-2">
                <View className="flex-row items-center gap-2" style={{ minHeight: 60 }}>
                  {otherAttentionGames.length > 1 && (
                    <TouchableOpacity
                      onPress={() => cycleAttentionGame(-1)}
                      accessibilityLabel="Previous game needing attention"
                      accessibilityRole="button"
                      testID="game-menu-turn-previous"
                      className="bg-paper-up border border-olive rounded-xl items-center justify-center"
                      style={{ minHeight: 48, minWidth: 40 }}
                    >
                      <Text className="text-ink font-stencil text-2xl">‹</Text>
                    </TouchableOpacity>
                  )}

                  <View className="flex-1" style={{ height: 60 }}>
                    {otherAttentionGames.length > 2 && (
                      <View
                        accessible={false}
                        className="absolute bg-paper border border-olive rounded-xl"
                        style={{ top: 8, left: 8, right: 0, bottom: 0 }}
                      />
                    )}
                    {otherAttentionGames.length > 1 && (
                      <View
                        accessible={false}
                        className="absolute bg-paper-up border border-olive rounded-xl"
                        style={{ top: 4, left: 4, right: 4, bottom: 4 }}
                      />
                    )}
                    <TouchableOpacity
                      onPress={() => {
                        dismissThenNavigate(() =>
                          router.replace(`/(app)/games/${activeAttentionGame.id}` as never),
                        )
                      }}
                      accessibilityLabel={`Open ${activeAttentionGame.name ?? 'game'}, ${activeGameIsInvite ? 'pending invitation' : 'your turn'}`}
                      accessibilityRole="button"
                      className="absolute bg-paper-up border-2 border-blood rounded-xl px-3 flex-row gap-2 items-center"
                      style={{
                        top: 0,
                        left: 0,
                        right: otherAttentionGames.length > 1 ? 8 : 0,
                        bottom: otherAttentionGames.length > 1 ? 8 : 0,
                        minHeight: 48,
                      }}
                    >
                      <View className="flex-1">
                        <Text className="text-blood font-stencil text-[10px] leading-[14px] uppercase tracking-wider">
                          {activeGameIsInvite ? 'Pending Invite' : 'Your Turn'}
                          {otherAttentionGames.length > 1
                            ? ` · ${attentionGameIndex % otherAttentionGames.length + 1}/${otherAttentionGames.length}`
                            : ''}
                        </Text>
                        <Text
                          className="text-ink font-stencil text-sm leading-5 uppercase"
                          numberOfLines={1}
                        >
                          {activeAttentionGame.name ?? 'Game'}
                        </Text>
                      </View>
                      <Text className="text-blood font-stencil text-xs uppercase">
                        {activeGameIsInvite ? 'Review →' : 'Play →'}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {otherAttentionGames.length > 1 && (
                    <TouchableOpacity
                      onPress={() => cycleAttentionGame(1)}
                      accessibilityLabel="Next game needing attention"
                      accessibilityRole="button"
                      testID="game-menu-turn-next"
                      className="bg-paper-up border border-olive rounded-xl items-center justify-center"
                      style={{ minHeight: 48, minWidth: 40 }}
                    >
                      <Text className="text-ink font-stencil text-2xl">›</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            )}

            <View
              testID="game-menu-current-game-divider"
              className="h-px bg-olive/50 mt-1"
            />
            <Text
              testID="game-menu-current-game-heading"
              className="text-olive-up font-stencil text-[9px] uppercase tracking-widest px-1"
              numberOfLines={1}
            >
              {currentGame?.name ?? 'Game'}
            </Text>

            {/* The log (filters, grown view) lives beside the chat input;
                the cog holds dice luck, config and resign. */}

            {onShowDiceLuck && (
              <TouchableOpacity
                onPress={() => dismissThenNavigate(onShowDiceLuck, { reuseHistoryEntry: true })}
                accessibilityLabel="View dice luck"
                accessibilityRole="button"
                className="bg-paper-up border border-olive rounded-xl px-4 py-3 flex-row gap-2 items-center justify-center"
                style={{ minHeight: 48 }}
              >
                <MaterialCommunityIcons
                  name="dice-multiple-outline"
                  size={18}
                  color={actionIconColor}
                  accessible={false}
                />
                <Text className="text-ink font-stencil uppercase">Dice Luck</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              onPress={() => {
                setForfeitStep(null)
                setSettingsOpen(false)
                setConfigOpen(true)
              }}
              accessibilityLabel={specialGame ? 'View special game config' : 'View game config'}
              accessibilityRole="button"
              className="bg-paper-up border border-olive rounded-xl px-4 py-3 flex-row gap-2 items-center justify-center"
              style={{ minHeight: 48 }}
            >
              <MaterialCommunityIcons
                name="clipboard-text-outline"
                size={18}
                color={actionIconColor}
                accessible={false}
              />
              <Text className="text-ink font-stencil uppercase">
                {specialGame ? 'Special Game Config' : 'Game Config'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={toggleTutorial}
              disabled={tutorialTogglePending}
              accessibilityLabel={
                tutorialTogglePending
                  ? 'Updating Inline Tips'
                  : tutorialEnabled
                    ? 'Hide Inline Tips'
                    : 'Show Inline Tips'
              }
              accessibilityRole="button"
              accessibilityState={{ disabled: tutorialTogglePending, busy: tutorialTogglePending }}
              className="bg-paper-up border border-olive rounded-xl px-4 py-3 flex-row gap-2 items-center justify-center"
              style={{ minHeight: 48 }}
            >
              {tutorialTogglePending ? (
                <ActivityIndicator size="small" color={actionIconColor} />
              ) : (
                <>
                  <MaterialCommunityIcons
                    name="lightbulb-outline"
                    size={18}
                    color={actionIconColor}
                    accessible={false}
                  />
                  <Text className="text-ink font-stencil uppercase">
                    {tutorialEnabled ? 'Hide Inline Tips' : 'Show Inline Tips'}
                  </Text>
                </>
              )}
            </TouchableOpacity>

            {canResign && (
              <TouchableOpacity
                onPress={() => setForfeitStep('choose')}
                accessibilityLabel="Forfeit Game"
                accessibilityRole="button"
                className="border-2 rounded-xl px-4 py-3 flex-row gap-2 items-center justify-center"
                style={{ minHeight: 48, ...resignColors }}
              >
                <Text accessible={false} className="text-ink text-xl">⚠︎</Text>
                <Text className="text-ink font-stencil uppercase">Forfeit Game</Text>
              </TouchableOpacity>
            )}

          </ScrollView>
          )}
      </FloatingGamePanel>
    </>
  )
}
